<template lang="pug">
div
    SearchWrapper(:searchContext="$search.dealerLocator")
        div(class="px-3 bg-gray-lighter")
            div(class="lg:container py-5")
                h1(class="text-center mt-0") Find a Local Dealer

                div(class="mx-auto w-full lg:max-w-md space-y-3 text-center")
                    BrandSelector(
                        v-if="$config.runtime.theme.dealerLocator.showBrandFacet"
                    )

                    div(class="field flex items-center h-10")
                        div(class="select h-full")
                            select(
                                type="select"
                                v-model="radius"
                                class="h-full"
                            )
                                option(value="5") 5 miles
                                option(value="10") 10 miles
                                option(value="25") 25 miles
                                option(value="50") 50 miles

                        div(class="flex-1 h-full")
                            //- hidden fields to throw off browser autocomplete
                            div(class="absolute" style="right:99999px;")
                                input(id="address" type="text", name="address")
                                input(id="city" type="text", name="city")
                                input(id="zip" type="text", name="zipcode")
                            input(
                                ref="placesSearch"
                                type="text"
                                placeholder="Search by City or Zip"
                                class="single-input w-full h-full rounded-r appearance-none"
                            )

                    NuxtLink(
                        v-if="$config.runtime.theme.dealerLocator.isInternationalEnabled"
                        :to="{ name: 'dealer-locator-international' }"
                        class="relative inline-block text-action font-bold ml-3 pr-6"
                    )
                        span(class="block triangle triangle-thick") or Select a Country


        div(class="p-3")
            div(class="lg:container")
                div(class="grid grid-cols-1 gap-4 lg:grid-cols-40-60 lg:gap-12 lg:pt-3")
                    div(class="lg:order-last lg:relative")
                        div(class="h-[340px] lg:sticky lg:top-3 lg:pb-6 lg:h-screen")
                            div(
                                ref="map"
                                class="w-full h-full bg-gray-lighter"
                            )

                    div
                        //- While initial loading
                        template(v-if="isLoading")
                            SpinnerText(
                                text="Loading"
                                isDark
                            )

                        //- if initial geocoding fails for any reason and we haven't searched yet
                        template(v-else-if="!hasSearched")
                            p Please enter your location above to find a dealer near you.

                        //- NOTE: We are explicitly not using the search components here and doing this from the ground up
                        //- because of the complex sorting that is required.
                        //- state when we have atleast a single location
                        template(v-else-if="results.length > 0")
                            p(class="mb-4") {{ numberize(results.length, 'Dealer') }} within {{ radius }} miles of {{ location }}

                            p #[span(class="text-danger-dark font-bold") Please note:] Not all dealers stock inventory. Please call dealer to confirm product availability.

                            ul(class="py-4")
                                template(v-for="(result, index) in results")
                                    li(
                                        :key="result.objectID"
                                        class="border-t border-black py-4"
                                    )
                                        Dealer(
                                            :dealer="result"
                                            addToMap
                                            @addMarker="addMarkerToMap"
                                            @focusMarker="focusMarkerOnMap"
                                        )

                        //- Fallback when we have nothing to show
                        template(v-else)
                            p Sorry, there are no locations within your search area. Please try expanding your search.

</template>

<script>
import SpinnerText from '@/components/spinnerText'
import SearchWrapper from '@/components/search/wrapper'
import SearchResults from '@/components/search/results'
import SearchNoResults from '@/components/search/noResults'
import { numberize } from '@/helpers/filters'
import BrandSelector from './_components/brandSelector'
import Dealer from './_components/dealer'

export default {
    name: 'LocalDealers',
    components: {
        SpinnerText,
        SearchWrapper,
        SearchResults,
        SearchNoResults,
        BrandSelector,
        Dealer,
    },
    data() {
        return {
            mapDefaults: {
                zoom: 4,
                center: { lat: 37.0902, lng: -95.7129 },
            },
            radius: 25,
            location: '',
            isLoading: true,
            hasSearched: false,
        }
    },
    head() {
        return this.$metaHelpers
            .generateMetas(
                {
                    title: 'Dealer Locator [TITLE_SUFFIX]',
                },
                this.$route
            )
            .format()
    },
    computed: {
        results() {
            return this.$search.dealerLocator.indexes
                .map((index) => {
                    if (index.key === 'brand' && index.state.activeRefinementCount === 0) return []
                    return index.state.results
                })
                .flat()
        },
    },
    watch: {
        radius() {
            this.search()
        },
        results(newVal) {
            // If we didn't find any results then reset the map back to the default view
            if (newVal.length === 0) {
                this.map.setOptions(this.mapDefaults)
                return
            }

            // Create bounding obj
            const bounds = new window.google.maps.LatLngBounds()
            // Add new results to the bounding obj
            newVal.forEach((result) => bounds.extend(result._geoloc))

            // Center map around new bounding location center
            this.map.setCenter(bounds.getCenter())

            // Make the map fit the bounds of the new data set.
            // The 20 will add an extra 20px around the edge of the map so none of the points are right on the edge of the map.
            this.map.fitBounds(bounds, 20)
        },
    },
    mounted() {
        this.$googlePlaces.load(() => {
            this.initGoogle()
        })
    },
    methods: {
        numberize,
        async initGoogle() {
            // Create map instance
            this.map = new window.google.maps.Map(this.$refs.map, {
                ...this.mapDefaults,
                maxZoom: 16,
                fullscreenControl: false,
                disableDefaultUI: false,
                keyboardShortcuts: false,
                mapTypeControl: false,
                streetViewControl: false,
            })

            // Create infoWindow instance
            this.infoWindow = this.infoWindow = new window.google.maps.InfoWindow({
                maxWidth: 290,
            })

            // Create Autocomplete widget instance
            this.autocomplete = new window.google.maps.places.Autocomplete(
                this.$refs.placesSearch,
                {
                    types: ['(regions)'],
                    componentRestrictions: { country: ['us', 'ca'] },
                }
            )

            // Add listener to the Autocomplete instance
            this.autocomplete.addListener('place_changed', () => {
                this.getLocationFromGoogle()
            })

            // Try to find current location
            await this.initLocation()
        },
        async initLocation() {
            // We can't do this if the geolocation feature isn't enabled
            if (!this.$geolocation.enabled || !this.$geolocation.zipcode) return

            // Create Geocoder instance if it doesn't exist
            if (!this.geocoder) this.geocoder = new window.google.maps.Geocoder()

            try {
                // Geocode zip into LatLng
                const { results } = await this.geocoder.geocode({
                    address: '',
                    componentRestrictions: { postalCode: this.$geolocation.zipcode },
                })

                // Get first result from geocode
                const [address] = results

                // Make sure we have an address before we try to set it
                if (!address) return

                // Set location to google result
                await this.setLocation(
                    address.geometry.location.lat(),
                    address.geometry.location.lng(),
                    address.formatted_address
                )
            } catch (e) {
                console.log('Unable to geocode location', e)
            } finally {
                this.isLoading = false
            }
        },
        async getLocationFromGoogle() {
            // Grab selected place from autocomplete field
            const place = this.autocomplete.getPlace()

            // Got place from autocomplete list
            if (place.geometry) {
                this.setLocation(
                    place.geometry.location.lat(),
                    place.geometry.location.lng(),
                    place.formatted_address
                )
            } else if (place.name) {
                // Create AutocompleteService instance
                if (!this.autocompleteService)
                    this.autocompleteService = new window.google.maps.places.AutocompleteService()

                // Create PlacesService instance
                if (!this.placesService)
                    this.placesService = new window.google.maps.places.PlacesService(this.map)

                try {
                    // Get place prediction
                    const { predictions } = await this.autocompleteService.getPlacePredictions({
                        input: place.name,
                        types: ['(regions)'],
                        componentRestrictions: { country: ['us', 'ca'] },
                    })

                    // get first result from predictions
                    const [prediction] = predictions

                    // Make sure we have a prediction before we try to get more info about it
                    if (!prediction) {
                        // By calling this without args it will clear out the current coords that we had
                        this.setLocation()
                        return
                    }

                    // Try to get more info about the place that that placePredictions found
                    this.placesService.getDetails(
                        {
                            placeId: prediction.place_id,
                            fields: ['geometry.location', 'formatted_address'],
                        },
                        (result, status) => {
                            if (status !== 'OK') return
                            this.setLocation(
                                result.geometry.location.lat(),
                                result.geometry.location.lng(),
                                result.formatted_address
                            )
                        }
                    )
                } catch (e) {
                    console.log('Unable to guess location', e)
                }
            }
        },
        async setLocation(lat, lng, location) {
            // Set the location values if the values aren't nullish otherwise fallback to 0, 0 so we don't get any results
            this.$search.dealerLocator.setLocation(lat ?? 0, lng ?? 0)

            // Only set the input location we get given a value
            if (location) {
                this.location = location
                this.$refs.placesSearch.value = location
            }

            // Trigger a new search
            await this.search()
        },
        async search() {
            // Set remaining options and search
            // The isInternational flag is applied to false at the index level
            await this.$search.dealerLocator.setRadius(this.radius).search()
            this.hasSearched = true
        },
        // Gets marker instance from the dealer result and appends it to the map
        addMarkerToMap(marker) {
            marker.setMap(this.map)
        },
        // Gets dealer focus event and focuses the dealer on the map
        focusMarkerOnMap(event) {
            const { marker, content } = event

            const bounds = new window.google.maps.LatLngBounds(marker.getPosition())

            // Center map around new bounding location center
            this.map.setCenter(bounds.getCenter())

            // Make the map fit the bounds of the new data set
            this.map.fitBounds(bounds)

            // Update info window to focused dealer
            this.infoWindow.setOptions({
                content,
            })

            // Set the infoWindow position to the marker
            this.infoWindow.open({
                anchor: marker,
            })
        },
    },
}
</script>

<style lang="stylus" scoped>
.select select
    border-right: none
    border-top-right-radius: 0
    border-bottom-right-radius: 0
</style>
